import { QueryClient } from '@tanstack/react-query'
import { Draft, produce } from 'immer'
import { AllOperationIds, OpalApiResponseDocument } from '../schema'
import { buildQueryKey } from './buildQueryKey'
import { QueryKeyIdentifiers, Resources } from './types'

export type CacheUpdateFn<TResponse> = (
  draftDocument: Draft<TResponse>
) => Draft<TResponse>

export const buildQueryHelpers = <
  TOperationId extends AllOperationIds,
  TQueryKeyIdentifier extends QueryKeyIdentifiers<TOperationId>,
>(
  resource: Resources,
  operationId: AllOperationIds | null
) => ({
  invalidateCache: (
    queryClient: QueryClient,
    identifiers: TQueryKeyIdentifier
  ) => {
    queryClient.invalidateQueries(
      buildQueryKey({ resource, operationId, identifiers })
    )
  },

  queryKey: (identifiers: TQueryKeyIdentifier) =>
    buildQueryKey({ resource, operationId, identifiers }),

  selectAs:
    <TSelectorResult = OpalApiResponseDocument<TOperationId, 200>>(
      selectorFn: (
        data: OpalApiResponseDocument<TOperationId, 200>
      ) => TSelectorResult
    ) =>
    (data: OpalApiResponseDocument<TOperationId, 200>) =>
      selectorFn(data),

  setQueryData: (
    queryClient: QueryClient,
    identifiers: QueryKeyIdentifiers<TOperationId>,
    updateFn: CacheUpdateFn<OpalApiResponseDocument<TOperationId, 200>>
  ) => {
    queryClient.setQueryData<
      OpalApiResponseDocument<TOperationId, 200> | undefined
    >(
      buildQueryKey({ resource, operationId, identifiers }),
      (responseDocument) =>
        produce(responseDocument, (draft) => {
          if (draft === undefined) return draft
          return updateFn(draft)
        })
    )
  },
})
